import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DatabaseService } from '../services/database.service';
import { AlertService } from '../services/alert.service';
import { UserService } from '../services/user.service';
import { OptionsService } from '../services/options.service';
import Papa from 'papaparse';

interface ProcessResult {
  success: boolean;
  processed?: number;
  duplicates?: number;
  error?: string;
}

interface MigrationResult {
  success: boolean;
  updated: number;
  skipped: number;
  errors: number;
  error?: string;
}

interface MigrationDiagnosticsItem {
  eventId: string;
  requestTitle: string;
  startDate: string | null;
  endDate: string | null;
  vodExpirationDate: string | null;
  requestorIsLegacy: boolean;
  requestorIsPartial: boolean;
  eventLeaderIsLegacy: boolean;
  eventLeaderIsPartial: boolean;
  requestorDisplay: string;
  eventLeaderDisplay: string;
  requestorStatus: string;
  eventLeaderStatus: string;
  requestorReason: string;
  eventLeaderReason: string;
}

interface ReasonCount {
  reason: string;
  count: number;
  percent: number;
}

// Result shape for transforming lists that may contain "other" (unmapped) values
interface MultiTransformResult {
  keys: string;      // comma separated mapped keys
  other: string;     // comma separated unmapped raw values
}

interface CsvRow {
  [key: string]: string;
}

interface MappedEvent {
  workflow: string;
  request_type: string;
  expected_number_of_participants: number;
  start_date: string | null;
  end_date: string | null;
  primary_presenting_location: string;
  other_location: string;
  recording_location: string;
  additional_detail_for_location: string;
  remote_presenting_location: string;
  requestor: string;
  event_leader: string;
  responsible_for_editing: string;
  event_requirements: string;
  set_items: string;
  event_description: string;
  presenters: string;
  audience: string;
  video_sharing_platforms: string;
  video_file_recipient: string;
  region: string;
  how_will_you_take_questions: string;
  is_video_on_demand_needed: boolean;
  vod_expiration_date: string | null;
  data_classification: string;
  retention_time: string;
  request_status: string;
  assigned_to: string;
  tech: string;
  high_profile_event: boolean;
  captioning: boolean;
  work_related_event: boolean;
  testing: boolean;
  tools_to_be_used: string;
  program_code: string;
  cust_number: string;
  studio_needs: string;
  broadcast_comments: string;
  virtual_events_url: string;
  brightcove_url: string;
  teams_url: string;
  send_to_archive: boolean;
  caption_editor: string;
  event_ownership_transferred: boolean;
  gm_video_attendees: string;
  venue_attendees: string;
  brightcove_attendees: string;
  virtual_events_attendees: string;
  teams_attendees: string;
  social_media_attendees: string;
  slack_attendees: string;
  audio_conference_attendees: string;
  gmvc_room_count: string;
  studio_services_used: boolean;
  request_title: string;
  other_recording_location: string;
  gm_video_url: string;
  audience_other: string;
  how_will_you_take_questions_other: string;
  tools_other: string;
  calendar_id: string;
  video_editing_assigned_to: string;
  responsible_for_editing_other: string;
  video_sharing_platforms_other: string;
}

@Component({
  selector: 'app-csv-upload-dialog',
  templateUrl: './csv-upload-dialog.component.html',
  styleUrls: ['./csv-upload-dialog.component.scss'],
  standalone: false
})
export class CsvUploadDialogComponent {
  selectedFile: File | null = null;
  processing = false;
  processResults: ProcessResult | null = null;
  existingEvents: any[] = [];

  // One-time people migration
  migrationProcessing = false;
  migrationResults: MigrationResult | null = null;
  migrationStep = '';
  migrationCurrent = 0;
  migrationTotal = 0;
  migrationProgress = 0;

  migrationDiagnosticsLoading = false;
  migrationDiagnosticsLastUpdated: Date | null = null;
  migrationDiagnostics = {
    total: 0,
    pending: [] as MigrationDiagnosticsItem[],
    notRelevant: [] as MigrationDiagnosticsItem[],
    migrated: [] as MigrationDiagnosticsItem[],
    pendingCount: 0,
    notRelevantCount: 0,
    migratedCount: 0,
    pendingPercent: 0,
    notRelevantPercent: 0,
    migratedPercent: 0
  };

  selectedReason = 'All';
  selectedPerson = 'All';
  reasonOptions: string[] = ['All'];
  readonly personOptions = ['All', 'Requestor', 'Event Leader'];
  selectedPersonData = 'All';
  readonly personDataOptions = ['All', 'Legacy string', 'Partial JSON'];

  private lastMigrationIssues: Record<string, { requestor?: string; eventLeader?: string; updateError?: string }> = {};
  
  // Progress tracking properties
  currentStep = '';
  currentRowNumber = 0;
  totalRows = 0;
  currentPersonName = '';
  progressPercentage = 0;
  primaryPresentingLocations: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<CsvUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private databaseService: DatabaseService,
    private alertService: AlertService,
    private userService: UserService,
    private optionsService: OptionsService
  ) {
    this.loadExistingEvents();
    this.loadLocationOptions();
  }

  private getGmidFromUserPrincipalName(userPrincipalName?: string): string {
    if (!userPrincipalName) return '';
    const local = userPrincipalName.split('@')[0]?.trim();
    if (!local) return '';
    return local.substring(0, 6).toLowerCase();
  }

  private isValidGmid(gmid: string): boolean {
    return /^[a-z0-9]{6}$/i.test((gmid || '').trim());
  }

  private extractEmailFromText(text: string): string {
    if (!text) return '';
    const match = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    return match ? match[0].trim() : '';
  }

  private extractGmidFromText(text: string): string {
    if (!text) return '';
    const parts = text.split(':').map((part) => part.trim()).filter(Boolean);
    const gmidFromParts = parts.find((part) => this.isValidGmid(part));
    if (gmidFromParts) return gmidFromParts.toLowerCase();

    const match = text.match(/\b[a-z0-9]{6}\b/i);
    return match ? match[0].toLowerCase() : '';
  }

  private extractLegacyPersonData(person: any): { email: string; gmid: string } {
    if (!person) return { email: '', gmid: '' };

    if (typeof person === 'string') {
      return {
        email: this.extractEmailFromText(person),
        gmid: this.extractGmidFromText(person)
      };
    }

    const emailRaw = (person.email || person.mail || '').toString().trim();
    const nameRaw = (person.name || '').toString().trim();
    const gmidRaw = (person.gmid || '').toString().trim();

    return {
      email: this.extractEmailFromText(emailRaw) || this.extractEmailFromText(nameRaw),
      gmid: this.extractGmidFromText(gmidRaw) || this.extractGmidFromText(nameRaw)
    };
  }

  private isPersonMissing(person: any): boolean {
    if (!person) return true;
    if (typeof person === 'string') return !person.trim();

    const email = (person.email || person.mail || '').toString().trim();
    const name = (person.name || '').toString().trim();
    const gmid = (person.gmid || '').toString().trim();

    return !email && !name && !gmid;
  }

  private getPersonStatusLabel(person: any): string {
    if (this.isPersonMissing(person)) return 'Missing';
    if (this.personNeedsMigration(person)) return 'Needs migration';
    return 'OK';
  }

  private formatPersonDisplay(person: any): string {
    if (!person) return '';
    if (typeof person === 'string') return person;

    const name = (person.name || '').toString().trim();
    const email = (person.email || person.mail || '').toString().trim();
    const gmid = (person.gmid || '').toString().trim();
    const parts = [name, email, gmid].filter((value) => !!value);

    return parts.join(' | ');
  }

  private getLegacyReason(person: any): string {
    if (this.isPersonMissing(person)) return 'Missing person data';

    if (typeof person === 'string') {
      const parsed = this.extractLegacyPersonData(person);
      if (!parsed.email && !parsed.gmid) return 'Missing email/GMID';
      return 'Needs migration';
    }

    const gmid = (person.gmid || '').toString().trim();
    const parsed = this.extractLegacyPersonData(person);
    if (!parsed.email && !parsed.gmid) return 'Missing email/GMID';
    if (gmid && !this.isValidGmid(gmid)) return 'Invalid GMID';

    return 'Needs migration';
  }

  private isLegacyPerson(person: any): boolean {
    return typeof person === 'string' && !!person.trim();
  }

  private isPartialJsonPerson(person: any): boolean {
    if (!person || typeof person !== 'object') return false;

    const name = (person.name || '').toString().trim();
    const email = (person.email || person.mail || '').toString().trim();
    const gmid = (person.gmid || '').toString().trim();
    const hasAny = !!(name || email || gmid);
    if (!hasAny) return false;

    const hasAll = !!name && !!email && this.isValidGmid(gmid);
    return !hasAll;
  }

  private getLastIssue(eventId: string, key: 'requestor' | 'eventLeader'): string {
    const issue = this.lastMigrationIssues[eventId];
    if (!issue) return '';
    const reason = issue[key] || '';
    if (reason) return reason;
    if (issue.updateError) return `Update API error: ${issue.updateError}`;
    return '';
  }

  private addReasonCount(map: Map<string, number>, reason: string) {
    const key = reason || 'Needs migration';
    map.set(key, (map.get(key) || 0) + 1);
  }

  private refreshReasonOptions(pending: MigrationDiagnosticsItem[]) {
    const reasonSet = new Set<string>();
    pending.forEach((item) => {
      const combinedReason = this.getCombinedReason(item);
      if (combinedReason) {
        reasonSet.add(combinedReason);
      }
      if (item.requestorStatus !== 'OK' && item.requestorReason) {
        reasonSet.add(item.requestorReason);
      }
      if (item.eventLeaderStatus !== 'OK' && item.eventLeaderReason) {
        reasonSet.add(item.eventLeaderReason);
      }
    });

    const options = ['All', ...Array.from(reasonSet).sort()];
    this.reasonOptions = options;
    if (!options.includes(this.selectedReason)) {
      this.selectedReason = 'All';
    }
  }

  private getCombinedReason(item: MigrationDiagnosticsItem): string {
    const requestorIssue = item.requestorStatus !== 'OK' ? item.requestorReason : '';
    const eventLeaderIssue = item.eventLeaderStatus !== 'OK' ? item.eventLeaderReason : '';

    if (requestorIssue && eventLeaderIssue) {
      if (requestorIssue === eventLeaderIssue) return requestorIssue;
      return 'Multiple issues';
    }

    return requestorIssue || eventLeaderIssue || '';
  }

  private getReasonForSelectedPerson(item: MigrationDiagnosticsItem): string {
    if (this.selectedPerson === 'Requestor') {
      return item.requestorStatus !== 'OK' ? item.requestorReason : '';
    }

    if (this.selectedPerson === 'Event Leader') {
      return item.eventLeaderStatus !== 'OK' ? item.eventLeaderReason : '';
    }

    return this.getCombinedReason(item);
  }

  getFilteredPending(): MigrationDiagnosticsItem[] {
    const pending = this.migrationDiagnostics.pending;
    if (!pending.length) return [];

    return pending.filter((item) => {
      const combinedReason = this.getCombinedReason(item);
      const matchesReason = this.selectedReason === 'All'
        || item.requestorReason === this.selectedReason
        || item.eventLeaderReason === this.selectedReason
        || combinedReason === this.selectedReason;

      if (!matchesReason) return false;

      if (this.selectedPerson === 'Requestor') {
        return item.requestorStatus !== 'OK'
          && (this.selectedReason === 'All' || item.requestorReason === this.selectedReason);
      }

      if (this.selectedPerson === 'Event Leader') {
        return item.eventLeaderStatus !== 'OK'
          && (this.selectedReason === 'All' || item.eventLeaderReason === this.selectedReason);
      }

      if (this.selectedPersonData === 'Legacy string') {
        return item.requestorIsLegacy || item.eventLeaderIsLegacy;
      }

      if (this.selectedPersonData === 'Partial JSON') {
        return item.requestorIsPartial || item.eventLeaderIsPartial;
      }

      return true;
    });
  }

  getFilteredPendingCount(): number {
    return this.getFilteredPending().length;
  }

  getReasonChartData(): ReasonCount[] {
    const pending = this.migrationDiagnostics.pending;
    if (!pending.length) return [];

    const counts = new Map<string, number>();
    const shouldIncludeReason = (reason: string) =>
      this.selectedReason === 'All' || reason === this.selectedReason;

    pending.forEach((item) => {
      const reason = this.getReasonForSelectedPerson(item);
      if (!reason) return;
      if (!shouldIncludeReason(reason)) return;
      this.addReasonCount(counts, reason);
    });

    const total = Array.from(counts.values()).reduce((sum, value) => sum + value, 0);
    if (!total) return [];

    return Array.from(counts.entries())
      .map(([reason, count]) => ({
        reason,
        count,
        percent: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count);
  }

  private readonly migrationCutoffDate = new Date('2025-10-31T00:00:00Z');

  private isBeforeMigrationCutoff(dateValue: string | null): boolean {
    if (!dateValue) return false;
    const parsed = new Date(dateValue);
    if (isNaN(parsed.getTime())) return false;
    return parsed.getTime() < this.migrationCutoffDate.getTime();
  }

  private isNotRelevantEvent(event: any): boolean {
    return this.isBeforeMigrationCutoff(event?.start_date ?? null)
      || this.isBeforeMigrationCutoff(event?.end_date ?? null)
      || this.isBeforeMigrationCutoff(event?.vod_expiration_date ?? null);
  }

  private computeMigrationDiagnostics(events: any[]) {
    const pending: MigrationDiagnosticsItem[] = [];
    const notRelevant: MigrationDiagnosticsItem[] = [];
    const migrated: MigrationDiagnosticsItem[] = [];

    (events || []).forEach((event: any) => {
      const isNotRelevant = this.isNotRelevantEvent(event);
      const needsMigration = this.personNeedsMigration(event?.requestor)
        || this.personNeedsMigration(event?.event_leader);

      const item: MigrationDiagnosticsItem = {
        eventId: String(event?.event_id || ''),
        requestTitle: (event?.request_title || '').toString(),
        startDate: event?.start_date ?? null,
        endDate: event?.end_date ?? null,
        vodExpirationDate: event?.vod_expiration_date ?? null,
        requestorIsLegacy: this.isLegacyPerson(event?.requestor),
        requestorIsPartial: this.isPartialJsonPerson(event?.requestor),
        eventLeaderIsLegacy: this.isLegacyPerson(event?.event_leader),
        eventLeaderIsPartial: this.isPartialJsonPerson(event?.event_leader),
        requestorDisplay: this.formatPersonDisplay(event?.requestor),
        eventLeaderDisplay: this.formatPersonDisplay(event?.event_leader),
        requestorStatus: this.getPersonStatusLabel(event?.requestor),
        eventLeaderStatus: this.getPersonStatusLabel(event?.event_leader),
        requestorReason: '',
        eventLeaderReason: ''
      };

      const eventId = item.eventId;
      const requestorIssue = this.getLastIssue(eventId, 'requestor');
      const eventLeaderIssue = this.getLastIssue(eventId, 'eventLeader');

      item.requestorReason = requestorIssue
        || (this.personNeedsMigration(event?.requestor) ? this.getLegacyReason(event?.requestor) : '');
      item.eventLeaderReason = eventLeaderIssue
        || (this.personNeedsMigration(event?.event_leader) ? this.getLegacyReason(event?.event_leader) : '');

      if (isNotRelevant) {
        notRelevant.push(item);
      } else if (needsMigration) {
        pending.push(item);
      } else {
        migrated.push(item);
      }
    });

    const total = (events || []).length;
    const pendingCount = pending.length;
    const notRelevantCount = notRelevant.length;
    const migratedCount = migrated.length;
    const pendingPercent = total ? Math.round((pendingCount / total) * 100) : 0;
    const notRelevantPercent = total ? Math.round((notRelevantCount / total) * 100) : 0;
    const migratedPercent = total ? Math.round((migratedCount / total) * 100) : 0;

    this.migrationDiagnostics = {
      total,
      pending,
      notRelevant,
      migrated,
      pendingCount,
      notRelevantCount,
      migratedCount,
      pendingPercent,
      notRelevantPercent,
      migratedPercent
    };

    this.refreshReasonOptions(pending);
  }

  private personNeedsMigration(person: any): boolean {
    if (!person) return false;
    if (typeof person === 'string') {
      const parsed = this.extractLegacyPersonData(person);
      return !!(parsed.email || parsed.gmid);
    }

    const parsed = this.extractLegacyPersonData(person);
    const name = (person.name || '').toString().trim();
    const gmid = (person.gmid || '').toString().trim();

    return !parsed.email || !name || !this.isValidGmid(gmid);
  }

  private async resolveUserByQuery(query: string, expectedEmail?: string): Promise<any | null> {
    if (!query) return null;
    const results = await new Promise<any[]>((resolve, reject) => {
      this.userService.searchPerson(query).subscribe({
        next: (res) => resolve(res || []),
        error: (err) => reject(err)
      });
    }).catch(() => [] as any[]);

    if (!results.length) return null;
    if (results.length === 1) return results[0];

    const expectedLower = (expectedEmail || '').toLowerCase();
    if (expectedLower) {
      return results.find((u: any) => (u.mail || '').toLowerCase() === expectedLower)
        || results.find((u: any) => (u.userPrincipalName || '').toLowerCase() === expectedLower)
        || results[0]
        || null;
    }

    if (query.includes('@')) {
      const queryLower = query.toLowerCase();
      return results.find((u: any) => (u.mail || '').toLowerCase() === queryLower)
        || results.find((u: any) => (u.userPrincipalName || '').toLowerCase() === queryLower)
        || results[0]
        || null;
    }

    const queryLower = query.toLowerCase();
    return results.find((u: any) => (u.userPrincipalName || '').split('@')[0]?.substring(0, 6).toLowerCase() === queryLower)
      || results[0]
      || null;
  }

  private buildPersonMeta(user: any, fallbackEmail: string) {
    const meta = {
      gmid: this.getGmidFromUserPrincipalName(user?.userPrincipalName),
      email: user?.mail || user?.userPrincipalName || fallbackEmail,
      name: user?.displayName || ''
    };

    if (!meta.name || !meta.email || !this.isValidGmid(meta.gmid)) {
      return null;
    }

    return meta;
  }

  private formatPersonPreview(person: any): string {
    if (!person) return 'N/A';
    if (typeof person === 'string') return person;
    return this.formatPersonDisplay(person) || 'N/A';
  }

  private async buildMigrationUpdate(event: any): Promise<{ payload: any; changed: boolean; issues: { requestor?: string; eventLeader?: string } }>{
    const issues: { requestor?: string; eventLeader?: string } = {};
    const updatePayload: any = { event_id: event?.event_id };
    let changed = false;

    if (this.personNeedsMigration(event?.requestor)) {
      const legacy = this.extractLegacyPersonData(event?.requestor);
      const query = legacy.email || legacy.gmid;
      if (query) {
        const user = await this.resolveUserByQuery(query, legacy.email);
        if (user) {
          const meta = this.buildPersonMeta(user, legacy.email);
          if (meta) {
            updatePayload.requestor = meta;
            changed = true;
          } else {
            issues.requestor = 'Missing name/GMID/email from Graph';
          }
        } else {
          issues.requestor = 'No Graph match for query';
        }
      } else {
        issues.requestor = 'Missing email/GMID';
      }
    }

    if (this.personNeedsMigration(event?.event_leader)) {
      const legacy = this.extractLegacyPersonData(event?.event_leader);
      const query = legacy.email || legacy.gmid;
      if (query) {
        const user = await this.resolveUserByQuery(query, legacy.email);
        if (user) {
          const meta = this.buildPersonMeta(user, legacy.email);
          if (meta) {
            updatePayload.event_leader = meta;
            changed = true;
          } else {
            issues.eventLeader = 'Missing name/GMID/email from Graph';
          }
        } else {
          issues.eventLeader = 'No Graph match for query';
        }
      } else {
        issues.eventLeader = 'Missing email/GMID';
      }
    }

    return { payload: updatePayload, changed, issues };
  }

  async migratePendingItem(item: MigrationDiagnosticsItem, clickEvent?: Event): Promise<void> {
    if (clickEvent) {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
    }

    if (this.processing || this.migrationProcessing) return;

    const event = this.existingEvents.find((evt) => String(evt?.event_id || '') === String(item.eventId));
    if (!event) {
      await this.alertService.error('Unable to locate the event for this row. Please refresh diagnostics and try again.');
      return;
    }

    this.migrationProcessing = true;
    this.migrationStep = `Preparing migration for event #${event?.event_id || 'unknown'}`;

    const update = await this.buildMigrationUpdate(event);
    if (!update.changed) {
      const reasonLines = [update.issues.requestor, update.issues.eventLeader].filter(Boolean);
      await this.alertService.warning(
        `No migration update is available for this record.${reasonLines.length ? '\n\n' + reasonLines.join('\n') : ''}`
      );
      this.migrationProcessing = false;
      this.migrationStep = '';
      return;
    }

    const requestorBefore = this.formatPersonPreview(event?.requestor);
    const requestorAfter = this.formatPersonPreview(update.payload.requestor);
    const eventLeaderBefore = this.formatPersonPreview(event?.event_leader);
    const eventLeaderAfter = this.formatPersonPreview(update.payload.event_leader);

    const messageLines = [
      `Event #${event?.event_id || 'unknown'}: ${event?.request_title || 'Untitled'}`,
      '',
      `Requestor: ${requestorBefore} -> ${requestorAfter}`,
      `Event Leader: ${eventLeaderBefore} -> ${eventLeaderAfter}`,
      '',
      'Apply this update?'
    ];

    const confirmed = await this.alertService.confirm(messageLines.join('\n'));
    if (!confirmed) {
      this.migrationProcessing = false;
      this.migrationStep = '';
      return;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        this.databaseService.updateEventById(String(event?.event_id || ''), update.payload).subscribe({
          next: () => resolve(),
          error: (err) => reject(err)
        });
      });

      await this.alertService.success('Migration applied for this event.');
      this.refreshMigrationDiagnostics();
    } catch (error: any) {
      const issues: { requestor?: string; eventLeader?: string; updateError?: string } = { ...update.issues };
      issues.updateError = 'Request failed';
      this.lastMigrationIssues[String(event?.event_id || '')] = issues;
      await this.alertService.error('Failed to migrate this event. Please try again.', this.alertService.getErrorDetails(error));
    } finally {
      this.migrationProcessing = false;
      this.migrationStep = '';
    }
  }

  async runPeopleMigration() {
    if (this.processing || this.migrationProcessing) return;

    const confirmed = await this.alertService.confirm(
      'This will update existing events that only have requestor/event leader email with the new JSON format (gmid, email, name). Continue?'
    );
    if (!confirmed) return;

    this.migrationProcessing = true;
    this.migrationResults = null;
    this.migrationStep = 'Loading events...';
    this.migrationCurrent = 0;
    this.migrationTotal = 0;
    this.migrationProgress = 0;

    try {
      this.lastMigrationIssues = {};
      const response = await new Promise<any>((resolve, reject) => {
        this.databaseService.getAllEvents().subscribe({
          next: (res) => resolve(res),
          error: (err) => reject(err)
        });
      });

      const events = Array.isArray(response) ? response : (response?.events || []);
      const candidates = events.filter((event: any) =>
        this.personNeedsMigration(event?.requestor) || this.personNeedsMigration(event?.event_leader)
      );

      this.migrationTotal = candidates.length;
      if (!candidates.length) {
        this.migrationResults = { success: true, updated: 0, skipped: 0, errors: 0 };
        this.migrationProcessing = false;
        return;
      }

      let updated = 0;
      let skipped = 0;
      let errors = 0;

      for (let i = 0; i < candidates.length; i++) {
        const event = candidates[i];
        const eventId = String(event?.event_id || '');
        const issues: { requestor?: string; eventLeader?: string; updateError?: string } = {};
        this.migrationCurrent = i + 1;
        this.migrationProgress = Math.round((this.migrationCurrent / this.migrationTotal) * 100);
        this.migrationStep = `Migrating event #${event?.event_id || 'unknown'} (${this.migrationCurrent}/${this.migrationTotal})`;

        const updatePayload: any = { event_id: event?.event_id };
        let changed = false;

        if (this.personNeedsMigration(event?.requestor)) {
          const legacy = this.extractLegacyPersonData(event?.requestor);
          const query = legacy.email || legacy.gmid;
          if (query) {
            const user = await this.resolveUserByQuery(query, legacy.email);
            if (user) {
              const meta = this.buildPersonMeta(user, legacy.email);
              if (meta) {
                updatePayload.requestor = meta;
                changed = true;
              } else {
                issues.requestor = 'Missing name/GMID/email from Graph';
              }
            } else {
              issues.requestor = 'No Graph match for query';
            }
          } else {
            issues.requestor = 'Missing email/GMID';
          }
        }

        if (this.personNeedsMigration(event?.event_leader)) {
          const legacy = this.extractLegacyPersonData(event?.event_leader);
          const query = legacy.email || legacy.gmid;
          if (query) {
            const user = await this.resolveUserByQuery(query, legacy.email);
            if (user) {
              const meta = this.buildPersonMeta(user, legacy.email);
              if (meta) {
                updatePayload.event_leader = meta;
                changed = true;
              } else {
                issues.eventLeader = 'Missing name/GMID/email from Graph';
              }
            } else {
              issues.eventLeader = 'No Graph match for query';
            }
          } else {
            issues.eventLeader = 'Missing email/GMID';
          }
        }

        if (!changed) {
          if (Object.keys(issues).length) {
            this.lastMigrationIssues[eventId] = issues;
          }
          skipped++;
          continue;
        }

        try {
          await new Promise<void>((resolve, reject) => {
            this.databaseService.updateEventById(String(event?.event_id || ''), updatePayload).subscribe({
              next: () => resolve(),
              error: (err) => reject(err)
            });
          });
          updated++;
        } catch {
          issues.updateError = 'Request failed';
          this.lastMigrationIssues[eventId] = issues;
          errors++;
        }
      }

      this.migrationResults = { success: errors === 0, updated, skipped, errors };
    } catch (error: any) {
      this.migrationResults = {
        success: false,
        updated: 0,
        skipped: 0,
        errors: 1,
        error: error?.message || 'Migration failed'
      };
    } finally {
      this.migrationProcessing = false;
      this.refreshMigrationDiagnostics();
    }
  }

  async loadExistingEvents() {
    this.migrationDiagnosticsLoading = true;
    try {
      this.databaseService.getAllEvents().subscribe({
        next: (response) => {
          // Handle response structure with count and events properties
          if (response && response.events && Array.isArray(response.events)) {
            this.existingEvents = response.events;
          } else if (Array.isArray(response)) {
            this.existingEvents = response;
          } else {
            this.existingEvents = [];
          }
          this.computeMigrationDiagnostics(this.existingEvents);
          this.migrationDiagnosticsLastUpdated = new Date();
          this.migrationDiagnosticsLoading = false;
        },
        error: (error) => {
          console.error('Error loading existing events:', error);
          this.existingEvents = [];
          this.computeMigrationDiagnostics([]);
          this.migrationDiagnosticsLoading = false;
        }
      });
    } catch (error) {
      console.error('Error loading existing events:', error);
      this.existingEvents = [];
      this.computeMigrationDiagnostics([]);
      this.migrationDiagnosticsLoading = false;
    }
  }

  refreshMigrationDiagnostics() {
    this.loadExistingEvents();
  }

  async loadLocationOptions() {
    try {
      this.optionsService.getAllOptions().subscribe({
        next: (options) => {
          this.primaryPresentingLocations = this.translateOptionsForUse(options, "Primary Presenting Locations");
          this.primaryPresentingLocations.push("I don't see my location");
          // console.log('Loaded primary presenting locations:', this.primaryPresentingLocations);
        },
        error: (error) => {
          console.error('Error loading location options:', error);
          this.primaryPresentingLocations = [];
        }
      });
    } catch (error) {
      console.error('Error loading location options:', error);
      this.primaryPresentingLocations = [];
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      this.selectedFile = file;
      this.processResults = null;
    } else if (file) {
      this.alertService.error('Please select a valid CSV file.');
      event.target.value = '';
    }
  }

  getFileSize(): string {
    if (!this.selectedFile) return '';
    const bytes = this.selectedFile.size;
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  async onProcess() {
    if (!this.selectedFile) return;

    this.processing = true;
    this.processResults = null;
    this.currentStep = 'Reading CSV file...';
    this.currentRowNumber = 0;
    this.totalRows = 0;
    this.progressPercentage = 0;

    try {
      const csvData = await this.readFileAsText(this.selectedFile);
      const parsedData = Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim()
      });

      if (parsedData.errors.length > 0) {
        throw new Error(`CSV parsing error: ${parsedData.errors[0].message}`);
      }

      const csvRows = parsedData.data as CsvRow[];
      this.totalRows = csvRows.length;
      
      // Filter duplicates BEFORE processing to save time
      this.currentStep = 'Filtering duplicate rows...';
      this.progressPercentage = 10;
      const uniqueCsvRows = this.filterDuplicateCsvRows(csvRows);
      
      this.totalRows = uniqueCsvRows.length;
      this.currentStep = 'Processing unique rows and looking up users...';

      // Process each unique row with person lookups
      const mappedEvents: MappedEvent[] = [];
      for (let i = 0; i < uniqueCsvRows.length; i++) {
        if (this.canceled) {
          this.currentStep = 'Upload canceled by user.';
          break;
        }
        this.currentRowNumber = i + 1;
        this.progressPercentage = 20 + (i / uniqueCsvRows.length) * 60; // 20-80% for row processing

        const row = uniqueCsvRows[i];
        this.currentStep = `Processing row ${this.currentRowNumber} of ${this.totalRows}`;
        
        const mappedEvent = await this.mapCsvRowToEvent(row, i + 1);
        mappedEvents.push(mappedEvent);
      }

      console.log('Mapped Events:', mappedEvents);

      this.currentStep = 'Final validation against database...';
      this.progressPercentage = 85;
      const uniqueEvents = this.filterDuplicatesAgainstDatabase(mappedEvents);
      
      this.currentStep = 'Uploading events to database one by one...';
      this.progressPercentage = 90;
      
      // Send each event individually to the database with encoding
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];
      
      for (let i = 0; i < uniqueEvents.length; i++) {
        if (this.canceled) {
          this.currentStep = 'Upload canceled by user.';
          break;
        }
        const event = uniqueEvents[i];
        this.currentStep = `Uploading event ${i + 1} of ${uniqueEvents.length}: ${event.request_title || 'Untitled'}`;
        this.progressPercentage = 90 + ((i / uniqueEvents.length) * 10);
        
        try {
          // Send individual event using createEvent (which includes encoding)
          await new Promise((resolve, reject) => {
            this.databaseService.createEvent(event).subscribe({
              next: (result) => {
                // console.log(`Event ${i + 1} uploaded successfully:`, result);
                successCount++;
                resolve(result);
              },
              error: (error) => {
                console.error(`Error uploading event ${i + 1}:`, error);
                errorCount++;
                errors.push(`Event ${i + 1} (${event.request_title || 'Untitled'}): ${error.message || 'Unknown error'}`);
                resolve(null); // Continue with next event instead of stopping
              }
            });
          });
          
          // Small delay between requests to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 100));
          
        } catch (error: any) {
          console.error(`Error uploading event ${i + 1}:`, error);
          errorCount++;
          errors.push(`Event ${i + 1} (${event.request_title || 'Untitled'}): ${error.message || 'Unknown error'}`);
        }
      }

      if (this.canceled) {
        this.processResults = {
          success: false,
          processed: successCount,
          duplicates: mappedEvents.length - uniqueEvents.length,
          error: 'Upload canceled by user.'
        };
        await this.alertService.warning(`Upload canceled by user. ${successCount} events were uploaded before cancellation.`);
        return;
      }
      
      this.progressPercentage = 100;
      this.currentStep = 'Complete!';
      
      this.processResults = {
        success: errorCount === 0,
        processed: successCount,
        duplicates: mappedEvents.length - uniqueEvents.length,
        error: errorCount > 0 ? `${errorCount} events failed to upload. ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}` : undefined
      };

      if (errorCount === 0) {
        await this.alertService.success(`Successfully uploaded ${successCount} events! All events processed successfully.`);
      } else if (successCount > 0) {
        await this.alertService.warning(`Partially successful: ${successCount} events uploaded, ${errorCount} failed. Check console for details.`);
      } else {
        await this.alertService.error(`Upload failed: ${errorCount} events failed to upload. Check console for details.`);
      }
      
      // Close the dialog after successful completion
      setTimeout(() => {
        this.dialogRef.close(true);
      }, 2000); // Wait 2 seconds to allow user to see success message
      
    } catch (error: any) {
      console.error('Error processing CSV:', error);
      this.processResults = {
        success: false,
        error: error.message || 'Failed to process CSV file'
      };
      await this.alertService.error(`Error processing CSV: ${error.message || 'Unknown error'}`);
    } finally {
      this.processing = false;
      this.currentStep = '';
      this.currentPersonName = '';
      this.currentRowNumber = 0;
      this.totalRows = 0;
      this.progressPercentage = 0;
    }
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  // Key -> Label maps (source of truth)
  private readonly AUDIENCE_MAP: Record<string, string> = {
    audAllGMAndBadgedContractors: 'All GM and Badged Contractors',
    audDealers: 'Dealers',
    audMedia: 'Media',
    audSuppliers: 'Suppliers',
    audGMFinancial: 'GM Financial',
    audPATAC: 'PATAC',
    audOtherNonGM: 'Other Non-GM'
  };

  private readonly QUESTIONS_MAP: Record<string, string> = {
    quesGMVideo: 'GM Video',
    quesEmail: 'Email',
    quesSlack: 'Slack',
    quesTeamsChat: 'Teams Chat',
    quesVirtualEventsQA: 'Virtual Events QA',
    quesPigeonhole: 'Pigeonhole',
    quesMSForms: 'MS Forms',
    quesUnsure: 'Unsure',
    quesNoQuestions: 'No Questions'
  };

  private readonly REGIONS_MAP: Record<string, string> = {
    regGMNA: 'GMNA',
    regGMSA: 'GMSA',
    regGMIO: 'GMIO',
    regCHINA: 'CHINA'
  };

  private readonly REQUIREMENTS_MAP: Record<string, string> = {
    eReqCamera: 'On Camera',
    eReqPowerPoint: 'PowerPoint',
    eReqVideos: 'Videos',
    eReqScreenSharing: 'Screen Sharing',
    eReqNA: 'NA'
  };

  private readonly RESPONSIBLE_EDITING_MAP: Record<string, string> = {
    rfeCommonwealth: 'Commonwealth',
    rfeJack: 'Jack Morton',
    rfeGMStudio: 'GM Studio Services',
    rfeMarting: 'Martin Retail',
    rfeMCCI: 'MCCI',
    rfeOne10: 'One10'
  };

  private readonly TOOLS_MAP: Record<string, string> = {
    toolTEAMS: 'TEAMS',
    toolTeamPolls: 'Team Polls',
    toolTeamsBreakoutRooms: 'Teams Breakout Rooms',
    toolGMVideo: 'GM Video',
    toolVirtualEvents: 'Virtual Events',
    toolWebcast: 'Webcast',
    toolDIYWebcast: 'DIY Webcast',
    toolPreRecordedSimulated: 'Pre-Recorded simulated',
    toolInteractiveSessions: 'Interactive Sessions',
    toolBrightcove: 'Brightcove',
    toolSocialMedia: 'Social Media',
    toolGMVC: 'GMVC',
    toolAudioConferencing: 'Audio Conferencing',
    toolLiveProduction: 'Live Production (i.e. Media Tour)'
  };

  private readonly VIDEO_SHARING_PLATFORMS_MAP: Record<string, string> = {
    vspSocrates: 'Socrates',
    vspBrightcove: 'Brightcove',
    vspExternal: 'External',
    vspMP4: 'MP4',
    vspGMVideo: 'GM Video',
    vspGlobalConnect: 'Global Connect'
  };

  // Build label->key indices (case-insensitive, stripped spaces/hyphens/parens)
  private buildReverseIndex(map: Record<string, string>): Record<string, string> {
    const idx: Record<string, string> = {};
    Object.entries(map).forEach(([key, label]) => {
      const norm = this.normalizeToken(label);
      idx[norm] = key;
    });
    return idx;
  }

  private normalizeToken(v: string): string {
    return v.toLowerCase()
      .replace(/[\s\-()]/g, '')      // remove spaces, hyphens, parentheses
      .replace(/&/g, 'and');         // unify ampersand
  }

  private transformList(csvVal: string, map: Record<string, string>, prefix: string): string {
    if (!csvVal || !csvVal.trim()) return '';
    const reverse = this.buildReverseIndex(map);
    // Accept existing comma, semicolon, or '#' separators
    const cleaned = csvVal.replace(/#/g, ';');
    const rawParts = cleaned.split(/[,;]+/).map(p => p.trim()).filter(p => p.length > 0);

    const resultKeys: string[] = [];

    rawParts.forEach(part => {
      // If already a key with prefix
      if (part.startsWith(prefix)) {
        if (map[part]) {
          resultKeys.push(part);
          return;
        }
      }

      // Normalize label for lookup
      const norm = this.normalizeToken(part);
      if (reverse[norm]) {
        resultKeys.push(reverse[norm]);
        return;
      }

      // Try relaxed match (remove slashes)
      const relaxed = norm.replace(/\//g, '');
      const foundRelaxed = Object.entries(map).find(([k, lbl]) =>
        this.normalizeToken(lbl).replace(/\//g, '') === relaxed
      );
      if (foundRelaxed) {
        resultKeys.push(foundRelaxed[0]);
        return;
      }

      console.error(`Unmatched value for prefix ${prefix}: "${part}"`);
    });

    // Deduplicate while preserving order
    const seen = new Set<string>();
    const deduped = resultKeys.filter(k => {
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    return deduped.join(', ');
  }

  

  // Merge existing *_other field from CSV with newly discovered unmatched values
  private mergeOther(existing: string, discovered: string): string {
    const parts = [...(existing ? existing.split(/[,;]+/).map(s => s.trim()).filter(Boolean) : []),
                   ...(discovered ? discovered.split(/[,;]+/).map(s => s.trim()).filter(Boolean) : [])];

    const unique: string[] = [];
    const seen = new Set<string>();
    for (const p of parts) {
      const low = p.toLowerCase();
      if (!seen.has(low)) {
        seen.add(low);
        unique.push(p);
      }
    }
    return unique.join(', ');
  }

  // Extended transform that also returns unmapped values
  private transformListWithOther(csvVal: string, map: Record<string, string>, prefix: string): MultiTransformResult {
    if (!csvVal || !csvVal.trim()) return { keys: '', other: '' };

    const reverse = this.buildReverseIndex(map);
    const cleaned = csvVal.replace(/#/g, ';');
    const rawParts = cleaned.split(/[,;]+/).map(p => p.trim()).filter(p => p.length > 0);

    const resultKeys: string[] = [];
    const unmatched: string[] = [];

    rawParts.forEach(part => {
      // Already a key
      if (part.startsWith(prefix) && map[part]) {
        if (!resultKeys.includes(part)) resultKeys.push(part);
        return;
      }

      const norm = this.normalizeToken(part);
      // Direct label match
      if (reverse[norm]) {
        const k = reverse[norm];
        if (!resultKeys.includes(k)) resultKeys.push(k);
        return;
      }

      // Relaxed match (strip slashes)
      const relaxed = norm.replace(/\//g, '');
      const foundRelaxed = Object.entries(map).find(([k, lbl]) =>
        this.normalizeToken(lbl).replace(/\//g, '') === relaxed
      );
      if (foundRelaxed) {
        if (!resultKeys.includes(foundRelaxed[0])) resultKeys.push(foundRelaxed[0]);
        return;
      }

      // Unmatched -> goes to other bucket
      unmatched.push(part);
    });

    return {
      keys: resultKeys.join(', '),
      other: unmatched.join(', ')
    };
  }

  // Updated transform wrappers (with "_other" capture)
  private transformAudienceWithOther(csvAudience: string): MultiTransformResult {
    return this.transformListWithOther(csvAudience, this.AUDIENCE_MAP, 'aud');
  }
  private transformQuestionsWithOther(csvQuestions: string): MultiTransformResult {
    return this.transformListWithOther(csvQuestions, this.QUESTIONS_MAP, 'ques');
  }
  private transformResponsibleForEditingWithOther(csvVal: string): MultiTransformResult {
    return this.transformListWithOther(csvVal, this.RESPONSIBLE_EDITING_MAP, 'rfe');
  }
  private transformToolsToBeUsedWithOther(csvVal: string): MultiTransformResult {
    return this.transformListWithOther(csvVal, this.TOOLS_MAP, 'tool');
  }
  private transformVideoSharingPlatformsWithOther(csvVal: string): MultiTransformResult {
    return this.transformListWithOther(csvVal, this.VIDEO_SHARING_PLATFORMS_MAP, 'vsp');
  }

  // Keep original simple key-only transforms (may be used elsewhere)
  private transformAudience(csvAudience: string): string {
    return this.transformList(csvAudience, this.AUDIENCE_MAP, 'aud');
  }
  private transformQuestions(csvQuestions: string): string {
    return this.transformList(csvQuestions, this.QUESTIONS_MAP, 'ques');
  }
  private transformToolsToBeUsed(csvVal: string): string {
    return this.transformList(csvVal, this.TOOLS_MAP, 'tool');
  }
  private transformResponsibleForEditing(csvVal: string): string {
    return this.transformList(csvVal, this.RESPONSIBLE_EDITING_MAP, 'rfe');
  }
  private transformVideoSharingPlatforms(csvVal: string): string {
    return this.transformList(csvVal, this.VIDEO_SHARING_PLATFORMS_MAP, 'vsp');
  }

  private transformRegion(csvRegion: string): string {
    return this.transformList(csvRegion, this.REGIONS_MAP, 'reg');
  }

  private transformEventRequirements(csvRequirements: string): string {
    return this.transformList(csvRequirements, this.REQUIREMENTS_MAP, 'eReq');
  }

  /**
   * Derive request_status from workflow with precedence:
   * 1. Canceled  (workflow contains 'canceled' or 'cancelled')
   * 2. Approved  (workflow contains 'approved')
   * 3. Unsubmitted (workflow is only 'new')
   * 4. Pending   (all other workflows not yet approved/canceled)
   */
  private computeRequestStatus(workflow: string): string {
    if (!workflow) return 'Unsubmitted';
    const wf = workflow.toLowerCase();
    if (wf.includes('canceled') || wf.includes('cancelled')) {
      return 'Canceled';
    }
    if (wf.includes('approved')) {
      return 'Approved';
    }
    // Tokenize by '->'
    const steps = wf.split('->').map(s => s.trim()).filter(s => s.length);
    if (steps.length === 1 && steps[0] === 'new') {
      return 'Unsubmitted';
    }
    return 'Pending';
  }

  /**
   * Compute VOD expiration date from end_date and retention_time.
   * Retention options: "1 Year", "2 Years", "3 Years", "Does Not Need to be Retained"
   * If "Does Not Need to be Retained" treat as 1 Year.
   * Returns ISO string or null if end date invalid.
   */
  private computeVodExpirationDate(endDateIso: string | null, retention: string): string | null {
    if (!endDateIso) return null;
    const end = new Date(endDateIso);
    if (isNaN(end.getTime())) {
      console.warn('Invalid end date for VOD expiration:', endDateIso);
      return null;
    }

    const r = (retention || '').toLowerCase();
    let years = 1;
    if (r.includes('3')) years = 3;
    else if (r.includes('2')) years = 2;
    else if (r.includes('does not')) years = 1; // treat as 1 year
    // else default already 1

    // Add years safely
    const result = new Date(end.getTime());
    result.setFullYear(result.getFullYear() + years);
    return result.toISOString();
  }

  private stringifyPerson(name: string, email: string): string {
    const cleanedEmail = (email || '').trim();
    const cleanedName = (name || '').trim();
    const gmid = cleanedEmail && cleanedEmail.includes('@')
      ? cleanedEmail.split('@')[0].toLowerCase()
      : cleanedName.toLowerCase();
    return JSON.stringify({
      gmid: gmid || '',
      email: cleanedEmail || '',
      name: cleanedName || ''
    });
  }

  private async mapCsvRowToEvent(row: CsvRow, rowNumber: number): Promise<MappedEvent> {
    const requestTitle = this.getValueOrDefault(row, 'Request Title', '');
        // Extract person names from the CSV - adjust these column names based on your actual CSV
    const requestorName = this.getValueOrDefault(row, 'Requestor', '');
    const eventLeaderName = this.getValueOrDefault(row, 'Event Leader', '');
    // const responsibleForEditingName = this.getValueOrDefault(row, 'Responsible for Editing', '');
    const assignedToName = this.getValueOrDefault(row, 'Assigned To', '');

    // Look up each person and get their userPrincipalName
    const requestorUPN = await this.lookupPersonUPN(requestorName, 'Requestor');
    const eventLeaderUPN = await this.lookupPersonUPN(eventLeaderName, 'Event Leader');
    // const responsibleForEditingUPN = await this.lookupPersonUPN(responsibleForEditingName, 'Responsible for Editing');
    const assignedToUPN = await this.lookupPersonUPN(assignedToName, 'Assigned To');
    const assignedTechUPN = await this.lookupPersonUPN(this.getValueOrDefault(row, 'Tech', ''), 'Tech');

    const retentionTime = this.getValueOrDefault(row, 'Retention Time', '1 Year');

    // Parse dates once
    const startDateIso = this.formatDate(this.getValueOrDefault(row, 'Start Date and Time', ''), 'Start Date and Time', requestTitle);
    const endDateIso   = this.formatDate(this.getValueOrDefault(row, 'End Date and Time', ''), 'End Date and Time', requestTitle);

    // Derive VOD expiration from end date + retention (DO NOT read CSV "VOD Expiration Date")
    const vodExpirationIso = this.computeVodExpirationDate(endDateIso, retentionTime);

    // Map CSV columns to database columns using your actual CSV structure
    const workflowValue = this.translateWorkflowStatus(this.getValueOrDefault(row, 'workflowStatus', 'New'));
    // Prepare multiselect transform results for "other" fields
    const audienceRes = this.transformAudienceWithOther(this.getValueOrDefault(row, 'Audience', ''));
    const questionsRes = this.transformQuestionsWithOther(this.getValueOrDefault(row, 'How will you take questions', ''));
    const responsibleForEditingRes = this.transformResponsibleForEditingWithOther(this.getValueOrDefault(row, 'Responsible for Editing', ''));
    const toolsRes = this.transformToolsToBeUsedWithOther(this.getValueOrDefault(row, 'ToolsToBeUsed', ''));
    const videoSharingPlatformsRes = this.transformVideoSharingPlatformsWithOther(this.getValueOrDefault(row, 'Video Sharing Platforms', ''));

    return {
      workflow: workflowValue,
      // Use derived request_status
      request_status: this.computeRequestStatus(workflowValue),
      request_type: this.getValueOrDefault(row, 'Request Type', 'Live Event'),
      expected_number_of_participants: parseInt(this.getValueOrDefault(row, 'Expected Number of Participants', '0')) || 0,
      start_date: startDateIso,
      end_date: endDateIso,
      primary_presenting_location: this.matchPresentingLocation(this.getValueOrDefault(row, 'Primary Presenting Location', '')),
      other_location: this.getValueOrDefault(row, 'Other Location', ''),
      recording_location: this.getValueOrDefault(row, 'Recording Location', ''),
      additional_detail_for_location: this.getValueOrDefault(row, 'Additional Detail For Location', ''),
      remote_presenting_location: this.getValueOrDefault(row, 'Remote Presenting Location', ''),
      set_items: this.getValueOrDefault(row, 'Set Items', ''),
      event_description: this.getValueOrDefault(row, 'Description', ''),
      presenters: this.getValueOrDefault(row, 'Presenters', ''),
      video_file_recipient: this.getValueOrDefault(row, 'Video File Recipient', ''),      
      is_video_on_demand_needed: this.parseBool(this.getValueOrDefault(row, 'Is Video On Demand (VOD) needed?', 'false')),
      vod_expiration_date: vodExpirationIso,
      data_classification: this.getValueOrDefault(row, 'Data Classification', 'GM Confedential'),
      retention_time: retentionTime,
      high_profile_event: this.parseBool(this.getValueOrDefault(row, 'High Profile Event', 'false')),
      captioning: this.parseBool(this.getValueOrDefault(row, 'Captioning', 'false')),
      work_related_event: this.parseBool(this.getValueOrDefault(row, 'Work Related Event', 'true')),
      testing: this.parseBool(this.getValueOrDefault(row, 'Testing', 'true')),
      program_code: this.getValueOrDefault(row, 'Program Code', ''),
      cust_number: this.getValueOrDefault(row, 'Cust#', ''),
      studio_needs: this.getValueOrDefault(row, 'Studio Needs', ''),
      broadcast_comments: this.getValueOrDefault(row, 'Broadcast Comments', ''),
      virtual_events_url: this.getValueOrDefault(row, 'Virtual Events URL', ''),
      brightcove_url: this.getValueOrDefault(row, 'BrightCove URL', ''),
      teams_url: this.getValueOrDefault(row, 'Teams URL', ''),
      send_to_archive: this.parseBool(this.getValueOrDefault(row, 'Send to Archive', 'false')),
      caption_editor: this.getValueOrDefault(row, 'Caption Editor', ''),
      event_ownership_transferred: this.parseBool(this.getValueOrDefault(row, 'Event Ownership Transferred', 'false')),
      gm_video_attendees: this.getValueOrDefault(row, 'GM Video Attendees', '0'),
      venue_attendees: this.getValueOrDefault(row, 'Venue Attendees', '0'),
      brightcove_attendees: this.getValueOrDefault(row, 'Brightcove Attendees', '0'),
      virtual_events_attendees: this.getValueOrDefault(row, 'Virtual Events Attendees', '0'),
      teams_attendees: this.getValueOrDefault(row, 'Teams Attendees', '0'),
      social_media_attendees: this.getValueOrDefault(row, 'Social Media Attendees', '0'),
      slack_attendees: this.getValueOrDefault(row, 'Slack Attendees', '0'),
      audio_conference_attendees: this.getValueOrDefault(row, 'Audio Conference Attendees', '0'),
      gmvc_room_count: this.getValueOrDefault(row, 'GMVC Room Count', '0'),
      studio_services_used: this.parseBool(this.getValueOrDefault(row, 'Studio Services Used', 'false')),
      request_title: requestTitle,
      other_recording_location: this.getValueOrDefault(row, 'Other Recording Location', ''),
      gm_video_url: this.getValueOrDefault(row, 'GM Video URL', ''),
      calendar_id: this.getValueOrDefault(row, 'Calendar ID', ''),
      video_editing_assigned_to: this.getValueOrDefault(row, 'Video Editing Assigned To', ''),
      // people
      requestor: this.stringifyPerson(requestorName, requestorUPN),
      event_leader: this.stringifyPerson(eventLeaderName, eventLeaderUPN),
      assigned_to: assignedToUPN,
      tech: assignedTechUPN,
      // people
      // multiselect
      audience: audienceRes.keys,
      audience_other: this.mergeOther(this.getValueOrDefault(row, 'Audience Other', ''), audienceRes.other),
      how_will_you_take_questions: questionsRes.keys,
      how_will_you_take_questions_other: this.mergeOther(this.getValueOrDefault(row, 'Questions Other', ''), questionsRes.other),
      region: this.transformRegion(this.getValueOrDefault(row, 'Region', '')),
      event_requirements: this.transformEventRequirements(this.getValueOrDefault(row, 'Event Requirements', '')),
      
      responsible_for_editing: responsibleForEditingRes.keys,
      responsible_for_editing_other: this.mergeOther(this.getValueOrDefault(row, 'Responsible for Editing Other', ''), responsibleForEditingRes.other),
      tools_to_be_used: toolsRes.keys,
      tools_other: this.mergeOther(this.getValueOrDefault(row, 'Tools Other', ''), toolsRes.other),
      video_sharing_platforms: videoSharingPlatformsRes.keys,
      video_sharing_platforms_other: this.mergeOther(this.getValueOrDefault(row, 'Video Sharing Platforms Other', ''), videoSharingPlatformsRes.other)
      // multiselect
    };
  }

  private async lookupPersonUPN(personName: string, fieldName: string): Promise<string> {
    if (!personName || personName.trim() === '') {
      return '';
    }

    try {
      this.currentPersonName = personName;
      this.currentStep = `Looking up ${fieldName}: ${personName}`;
      
      const searchResults = await new Promise<any[]>((resolve, reject) => {
        this.userService.searchPerson(personName).subscribe({
          next: (results) => resolve(results),
          error: (error) => reject(error)
        });
      });

      if (searchResults && searchResults.length > 0) {
        // Return the userPrincipalName of the first match
        // console.log(`Found user for ${personName}:`, searchResults[0]);
        return searchResults[0].mail || personName;
      } else {
        console.error(`No user found for: ${personName} on current step: ${this.currentStep} and event title `);
        return personName; // Return original name if no match found
      }
    } catch (error) {
      console.error(`Error looking up user: ${personName} on current step: ${this.currentStep}`, error);
      return personName; // Return original name if lookup fails
    }
  }

  private getValueOrDefault(row: CsvRow, key: string, defaultValue: string): string {
    // Try multiple possible column name variations
    const possibleKeys = [
      key,
      key.toLowerCase(),
      key.replace(/\s+/g, '_'),
      key.replace(/\s+/g, '_').toLowerCase(),
      key.replace(/\s+/g, ''),
      key.replace(/\s+/g, '').toLowerCase()
    ];

    for (const possibleKey of possibleKeys) {
      if (row[possibleKey] !== undefined && row[possibleKey] !== null && row[possibleKey] !== '') {
        return row[possibleKey].toString().trim();
      }
    }

    return defaultValue;
  }

  private formatDate(dateStr: string, dateType: string, eventTitle: string): string | null {
    if (!dateStr || dateStr.trim() === '') return null;
    
    try {
      // console.log('Processing date string:', dateStr);
      // Try parsing the date and formatting it to ISO string
      const date = new Date(dateStr);
      // console.log('Parsed date object:', date, 'Year:', date.getFullYear());
      
      // Check if the date is valid and within reasonable range
      if (!isNaN(date.getTime()) && 
          date.getFullYear() > 1900 && 
          date.getFullYear() < 3000) {
        const isoDate = date.toISOString();
        // console.log('Formatted ISO date:', isoDate);
        return isoDate;
      } else {
        console.error(dateType, 'out of valid range or invalid:', dateStr, 'Parsed as:', date, 'for event', eventTitle);
        return null; // Return null for invalid dates
      }
    } catch (error) {
      console.error('Invalid date format:', dateStr, error);
      return null; // Return null for unparseable dates
    }
  }

  private parseBool(value: string): boolean {
    const lowerValue = value.toLowerCase().trim();
    return lowerValue === 'true' || lowerValue === '1' || lowerValue === 'yes';
  }

  /**
   * Translate CSV workflow status to proper workflow state
   */
  private translateWorkflowStatus(csvWorkflow: string): string {
    if (!csvWorkflow) return 'New -> Submitted';
    
    const workflow = csvWorkflow.trim();
    
    switch (workflow) {
      case 'Submitted':
        return 'New -> Submitted';
      case 'Cancelled':
        return 'New -> Submitted -> Canceled';
      case 'Canceled': // Handle both spellings
        return 'New -> Submitted -> Canceled';
      case 'Approved':
        return 'New -> Submitted -> SendToSchedule -> Approved';
      case 'SentToSchedule':
        return 'New -> Submitted -> SendToSchedule';
      case 'Not Submitted':
        return 'New';
      case 'KalturaSent':
        return 'New -> Submitted -> SentToSchedule -> Approved -> LinkSent';
      default:
        console.error('Unknown workflow status from CSV:', workflow, 'Using default: Event Workflow');
        return 'New -> Submitted';
    }
  }

  /**
   * Translate options from database format for use
   */
  translateOptionsForUse(options: any, name: string) {
    const optionEntry = options.find((opt: any) => opt.name === name);
    if (optionEntry) {
      if (optionEntry.isObject) {
        return JSON.parse(optionEntry.value);
      } else {
        return JSON.parse(optionEntry.value).arr.split(',');
      }
    } else {
      console.error(`Option with name "${name}" not found.`);
      return [];
    }
  }

  /**
   * Match CSV location value against database location options
   */
  private matchPresentingLocation(csvLocation: string): string {
    if (!csvLocation || csvLocation.trim() === '') return '';
    
    const csvLocationLower = csvLocation.toLowerCase().trim();
    
    // Find exact match first
    const exactMatch = this.primaryPresentingLocations.find(
      (location: any) => location.toLowerCase() === csvLocationLower
    );
    
    if (exactMatch) {
      // console.log(`Exact match found for location: "${csvLocation}" -> "${exactMatch}"`);
      return exactMatch;
    }
    
    // Find partial match
    const partialMatch = this.primaryPresentingLocations.find(
      (location: any) => 
        location.toLowerCase().includes(csvLocationLower) || 
        csvLocationLower.includes(location.toLowerCase())
    );
    
    if (partialMatch) {
      // console.log(`Partial match found for location: "${csvLocation}" -> "${partialMatch}"`);
      return partialMatch;
    }
    
    // No match found, return original value
    console.error(`No match found for location: "${csvLocation}". Available options:`, this.primaryPresentingLocations);
    return csvLocation;
  }

  private filterDuplicates(events: MappedEvent[]): MappedEvent[] {
    const uniqueEvents: MappedEvent[] = [];
    const seenEvents = new Set<string>();

    for (const event of events) {
      // Create a unique key based on title and start date
      const key = `${event.request_title?.toLowerCase()}-${event.start_date}`;
      
      // Check against existing events from database
      const existsInDb = this.existingEvents.some((existingEvent: any) => {
        const existingKey = `${existingEvent.request_title?.toLowerCase()}-${existingEvent.start_date}`;
        return existingKey === key;
      });

      // Check against events in current batch
      if (!seenEvents.has(key) && !existsInDb) {
        seenEvents.add(key);
        uniqueEvents.push(event);
      }
    }

    return uniqueEvents;
  }

  private filterDuplicateCsvRows(csvRows: CsvRow[]): CsvRow[] {
    const uniqueRows: CsvRow[] = [];
    const seenKeys = new Set<string>();

    for (const row of csvRows) {
      // Create a unique key based on title and start date (same as final duplicate check)
      const title = this.getValueOrDefault(row, 'Request Title', '').toLowerCase();
      const startDate = this.getValueOrDefault(row, 'Start Date and Time', '');
      const key = `${title}-${startDate}`;

      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        uniqueRows.push(row);
      }
    }

    return uniqueRows;
  }

  private filterDuplicatesAgainstDatabase(events: MappedEvent[]): MappedEvent[] {
    const uniqueEvents: MappedEvent[] = [];
    const seenEvents = new Set<string>();

    for (const event of events) {
      // Create a unique key based on title and start date
      const key = `${event.request_title?.toLowerCase()}-${event.start_date}`;
      
      // Check against existing events from database
      const existsInDb = this.existingEvents.some((existingEvent: any) => {
        const existingKey = `${existingEvent.request_title?.toLowerCase()}-${existingEvent.start_date}`;
        return existingKey === key;
      });

      // Check against events in current batch
      if (!seenEvents.has(key) && !existsInDb) {
        seenEvents.add(key);
        uniqueEvents.push(event);
      }
    }

    return uniqueEvents;
  }

  canceled = false;
  onCancel() {
    this.canceled = true;
    this.dialogRef.close();

  }
}